import React, { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data } = useData();
  const { selectedLocation } = useOutletContext() || {};

  const locationFilter = useMemo(() => {
    if (user?.role === 'manager') return (user.location || '').toLowerCase();
    if (selectedLocation && selectedLocation !== 'all') return selectedLocation.toLowerCase();
    return '';
  }, [selectedLocation, user?.location, user?.role]);

  const visibleUsers = useMemo(() => {
    const list = data.users || [];
    if (!locationFilter) return list;
    return list.filter((u) => (u.location || '').toLowerCase() === locationFilter);
  }, [data.users, locationFilter]);

  const visibleCourses = useMemo(() => {
    const list = data.courses || [];
    if (!locationFilter) return list;
    return list.filter((c) => (c.location || '').toLowerCase() === locationFilter);
  }, [data.courses, locationFilter]);

  const visibleEnrollments = useMemo(() => {
    const list = data.enrollments || [];
    if (!locationFilter) return list;
    return list.filter((e) => (e.course?.location || '').toLowerCase() === locationFilter);
  }, [data.enrollments, locationFilter]);

  const stats = {
    users: visibleUsers.length,
    courses: visibleCourses.length,
    enrollments: visibleEnrollments.length,
    studies: (data.studies || []).length
  };

  const roleLabel = t(`roles.${user?.role || 'student'}`);
  const locationLabel = user?.location
    ? translateLocation(t, String(user.location).toLowerCase())
    : t('admin.allLocations');

  const roleMatrix = useMemo(() => {
    if (user?.role === 'admin') {
      return {
        allowed: [
          'Gebruikers en rollen beheren (inclusief activeren/deactiveren).',
          'Studies, cohorten, betalingen en website-content beheren.',
          'Documenten uploaden via Modulematerialen, Blog en Brochures.'
        ],
        blocked: []
      };
    }

    if (user?.role === 'manager') {
      return {
        allowed: [
          'Gebruikers beheren binnen eigen locatie (student/docent).',
          'Studies, inschrijvingen, betalingen en rapporten voor eigen locatie.',
          'Documenten en lesmateriaal beheren binnen locatiegebonden vakken.'
        ],
        blocked: [
          'Geen toegang tot admin-only acties zoals systeemrollen wijzigen.',
          'Geen toegang tot data buiten eigen locatie.'
        ]
      };
    }

    return {
      allowed: [
        'Huiswerk en cijfers beheren voor eigen toegewezen vakken.',
        'Eigen vakken en planning raadplegen.',
        'Lesmateriaal uploaden voor eigen vakken.'
      ],
      blocked: [
        'Geen toegang tot gebruikersbeheer, betalingen en website CMS.',
        'Geen toegang tot vakken die niet aan de docent gekoppeld zijn.'
      ]
    };
  }, [user?.role]);

  const menuItems = useMemo(() => {
    if (user?.role === 'admin') {
      return [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'studies', title: t('admin.manageStudies'), path: '/admin/studies' },
        { key: 'assignments', title: t('admin.courseAssignments'), path: '/admin/course-assignments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
        { key: 'content', title: t('admin.websiteContent'), path: '/admin/content' }
      ];
    }

    if (user?.role === 'manager') {
      return [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'registrations', title: t('admin.studyRegistrations'), path: '/admin/study-registrations' },
        { key: 'assignments', title: t('admin.courseAssignments'), path: '/admin/course-assignments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
        { key: 'grades', title: t('report.gradesReport', 'Cijfers Rapport'), path: '/admin/report-grades' }
      ];
    }

    return [
      { key: 'homework', title: t('nav.homework'), path: '/admin/homework' },
      { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
      { key: 'calendar', title: t('admin.calendar'), path: '/admin/calendar' },
      { key: 'grades', title: t('report.gradesReport', 'Cijfers Rapport'), path: '/admin/report-grades' }
    ];
  }, [t, user?.role]);

  const uploadChecks = useMemo(() => ([
    {
      label: 'Admin modulematerialen',
      route: '/api/module-materials',
      storage: 'Backendsrv/uploads/module-materials/<courseId>/'
    },
    {
      label: 'Admin blog afbeeldingen',
      route: '/api/blogs',
      storage: 'Backendsrv/uploads/blogs/'
    },
    {
      label: 'Admin brochures/flyers',
      route: '/api/brochures en /api/content/flyers',
      storage: 'Backendsrv/uploads/brochures/ en Backendsrv/uploads/flyers/'
    },
    {
      label: 'Publieke inschrijvingsbijlagen',
      route: '/api/public/registration',
      storage: 'Backendsrv/uploads/registrations/'
    }
  ]), []);

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>{t('admin.dashboard')}</h1>
        <p className="welcome-text">{t('admin.welcome', { name: user?.fullName || user?.firstName || '-' })}</p>
        <div className="dashboard-meta-row">
          <span className="dashboard-meta-pill">Rol: {roleLabel}</span>
          <span className="dashboard-meta-pill">Locatie: {locationLabel}</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-icon">U</span>
          <div className="stat-info">
            <h3>{stats.users}</h3>
            <p>{t('admin.users')}</p>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">V</span>
          <div className="stat-info">
            <h3>{stats.courses}</h3>
            <p>{t('admin.courses')}</p>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">I</span>
          <div className="stat-info">
            <h3>{stats.enrollments}</h3>
            <p>{t('admin.enrollments')}</p>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">S</span>
          <div className="stat-info">
            <h3>{stats.studies}</h3>
            <p>{t('admin.studies')}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <section className="quick-actions">
          <h2>{t('admin.quickActions')}</h2>
          <div className="courses-list">
            {menuItems.map((item) => (
              <Link key={item.key} to={item.path} className="course-card">
                <div className="course-header">
                  <span className="course-name">{item.title}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="recent-section">
          <h2>Rechten Check ({roleLabel})</h2>
          <div className="rights-grid">
            <article className="rights-card rights-allowed">
              <h3>Toegestaan</h3>
              <ul>
                {roleMatrix.allowed.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="rights-card rights-blocked">
              <h3>Niet toegestaan</h3>
              {roleMatrix.blocked.length ? (
                <ul>
                  {roleMatrix.blocked.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>Geen functionele blokkades binnen admin-rolniveau.</p>
              )}
            </article>
          </div>
        </section>

        <section className="recent-section upload-check-section">
          <h2>Document Upload Check</h2>
          <p className="upload-check-note">
            Uploads worden via backend opgeslagen onder de `Backendsrv/uploads/` structuur en via `/api/uploads/...` ontsloten.
          </p>
          <div className="upload-check-list">
            {uploadChecks.map((item) => (
              <article key={item.label} className="upload-check-item">
                <h3>{item.label}</h3>
                <p><strong>Route:</strong> {item.route}</p>
                <p><strong>Opslag:</strong> {item.storage}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
